import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Award, Compass, BarChart3, ChevronRight, CheckCircle2 } from 'lucide-react';
import { usePageMeta } from '../../../hooks/useSEO';
import BreadcrumbSchema from '../../../components/seo/BreadcrumbSchema';
import { FinalCTA } from './DubaiCostOfLivingPage';

const PILLARS = [
  { icon: Compass,   title: 'End-to-End Support', copy: 'From profile shortlisting and SOP design to KHDA visa stamping, biometrics, and post-landing settling-in — one accountable counsellor, one timeline, one inbox. No middle-men, no hand-offs.' },
  { icon: Award,     title: 'Ethical Consulting', copy: 'MEA-approved (RC: B-0762/DEL/PER/1000+/5/9148/2015). Zero shortcuts on GTE, document verification, or sponsor declarations. Your file is built to clear Tier-1 immigration scrutiny — not just to pass intake screening.' },
  { icon: BarChart3, title: 'Data-Driven Guidance', copy: 'Live KHDA tuition feeds, UAE labour-market demand indices, and 10,000+ historic placement outcomes feed every recommendation. We don\u2019t guess your fit — we model it.' },
];

export default function DubaiAboutZafcoPage() {
  usePageMeta(
    'About ZAFCO Study Abroad Dubai — Career Migration Ecosystem',
    'How ZAFCO evolved from a global mobility consultancy into a 2026 Career Migration Ecosystem aligned with the Dubai D33 labour market. Career-first approach, MEA-approved ethical consulting, and 10,000+ placements.'
  );

  /* AboutPage JSON-LD — references sitewide #organization (no duplicate node). */
  useEffect(() => {
    const TAG = 'dubai-about-zafco-org-schema';
    document.getElementById(TAG)?.remove();
    const ld = {
      '@context': 'https://schema.org',
      '@type': 'AboutPage',
      '@id': 'https://zafco.in/study-abroad/dubai/about-zafco/#webpage',
      url: 'https://zafco.in/study-abroad/dubai/about-zafco/',
      name: 'About ZAFCO Study Abroad Dubai',
      isPartOf: { '@id': 'https://zafco.in/#website' },
      about: { '@id': 'https://zafco.in/#organization' },
      mainEntity: { '@id': 'https://zafco.in/#organization' },
      inLanguage: 'en-IN',
      description:
        'How ZAFCO evolved from a global mobility consultancy into a 2026 Career Migration Ecosystem aligned with the Dubai D33 labour market.',
    };
    const s = document.createElement('script');
    s.id = TAG; s.type = 'application/ld+json'; s.text = JSON.stringify(ld);
    document.head.appendChild(s);
    return () => document.getElementById(TAG)?.remove();
  }, []);

  return (
    <div className="zaf-edu-platform bg-white text-slate-900" data-testid="dubai-about-zafco-page">
      <BreadcrumbSchema items={[
        { name: 'Home', url: 'https://zafco.in/' },
        { name: 'Study Abroad', url: 'https://zafco.in/study-abroad/' },
        { name: 'Study in Dubai', url: 'https://zafco.in/study-abroad/dubai/' },
        { name: 'About ZAFCO', url: 'https://zafco.in/study-abroad/dubai/about-zafco/' },
      ]} />

      <section className="bg-gradient-to-br from-[#0F1B2C] to-[#162538] text-white py-12 md:py-16">
        <div className="container max-w-5xl mx-auto px-4 sm:px-6 md:px-12">
          <Link to="/study-abroad/dubai/" className="inline-flex items-center gap-2 text-[#D4AF37] text-xs font-semibold uppercase tracking-widest mb-4 hover:text-amber-300">
            <ChevronRight className="w-3 h-3 rotate-180" /> Dubai Hub
          </Link>
          <p className="text-[#D4AF37] text-xs font-bold uppercase tracking-widest mb-2">About ZAFCO Study Abroad</p>
          <h1 className="text-3xl md:text-5xl font-bold font-syne leading-tight">From Global Mobility to <span className="text-[#D4AF37]">Career Migration Ecosystem</span></h1>
          <p className="mt-5 text-slate-300 max-w-3xl leading-relaxed">ZAFCO began in 2015 as a Government-of-India approved overseas mobility consultancy. In 2026 — under Dubai&apos;s D33 economic agenda and the global skill-shortage wave — we&apos;ve rebuilt our practice into a <strong className="text-white">Career Migration Ecosystem</strong>: a single platform that engineers the entire arc from admission to long-term residency.</p>
        </div>
      </section>

      <section className="py-12 md:py-16">
        <div className="container max-w-5xl mx-auto px-4 sm:px-6 md:px-12">
          <h2 className="text-2xl md:text-3xl font-bold font-syne mb-3">Career-First Approach</h2>
          <p className="text-slate-700 leading-relaxed mb-4 max-w-3xl">Most consultants stop at the offer letter. We don&apos;t. Every Dubai student profile we accept is reverse-engineered from a <strong>target 2030 employer</strong> — DIFC, G42, DEWA, Emaar, Mashreq — and the residency pathway needed to land there. Course shortlists, university tier, internship cluster, and Golden Visa eligibility are decided together, not in isolation.</p>

          <h3 className="text-xl font-bold font-syne text-[#0F1B2C] mt-8 mb-3">2026 Labour Market Alignment</h3>
          <p className="text-slate-700 leading-relaxed mb-6 max-w-3xl">Our admissions desk syncs weekly with UAE MoHRE skill-shortage releases, KHDA tuition filings, and Dubai DIFC / ADGM hiring indices. The result: every recommendation is aligned with where the market is heading in 2026–2030, not where it was in 2019.</p>

          <h2 className="text-2xl md:text-3xl font-bold font-syne mt-10 mb-5">The 3-Pillar Model</h2>
          <div className="grid md:grid-cols-3 gap-5">
            {PILLARS.map((p) => (
              <article key={p.title} className="bg-slate-50 border border-slate-200 rounded-2xl p-6 hover:border-[#D4AF37] transition-colors" data-testid={`pillar-${p.title.split(' ')[0].toLowerCase()}`}>
                <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/15 flex items-center justify-center mb-3"><p.icon className="w-6 h-6 text-[#D4AF37]" /></div>
                <h3 className="font-bold font-syne text-[#0F1B2C] mb-2">{p.title}</h3>
                <p className="text-sm text-slate-700 leading-relaxed">{p.copy}</p>
              </article>
            ))}
          </div>

          <div className="mt-10 grid sm:grid-cols-4 gap-4 text-center">
            {[
              { value: '10,000+', label: 'Verified Placements' },
              { value: '500+',    label: 'Partner Universities' },
              { value: '98%+',    label: 'Visa Success Rate' },
              { value: '2015',    label: 'MEA-Approved' },
            ].map((stat) => (
              <div key={stat.label} className="bg-[#0F1B2C] text-white rounded-2xl p-5">
                <p className="text-3xl font-bold text-[#D4AF37] font-syne">{stat.value}</p>
                <p className="text-xs uppercase tracking-widest text-slate-400 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>

          <ul className="mt-10 space-y-2.5 max-w-3xl">
            {[
              'Single-counsellor accountability from enquiry to Emirates ID',
              'KHDA, MoHRE & ICP-compliant document architecture',
              'Direct relationships with admissions deans at 12+ Dubai campuses',
              'Free 90-day post-landing support — banking, housing, MoHRE work-permit',
              'Transparent fee structure — refund guarantee on visa rejection',
            ].map((bullet) => (
              <li key={bullet} className="flex gap-2 text-slate-800"><CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" /> {bullet}</li>
            ))}
          </ul>
        </div>
      </section>

      <FinalCTA source="dubai-about-zafco" />
    </div>
  );
}
